/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionAddSmartSearchFiltersFromAggregate';

const ActionAddSmartSearchFiltersFromAggregateManager: ActionManagerFn<
  'ActionAddSmartSearchFiltersFromAggregate'
> = (
  platform: Platform,
): ActionManager<'ActionAddSmartSearchFiltersFromAggregate'> => {
  return {
    ...baseActionManager(platform, 'ActionAddSmartSearchFiltersFromAggregate'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Datasource Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['ReadOnlyDataStore', 'DataStore', 'LocalDataStore'],
      },
      clearExistingFilters: {
        label: 'Clear Existing Filters',
        type: 'boolean',
      },
    },
    isAllowedInsideParent: (page, parent) => {
      return 'TriggerOnAggregateClick' === parent.type;
    },
    name: 'Add Smart Search Filters From Aggregate',
    icon: { iconName: 'copy' },
    description: `**Overview**<br/>
The "Add Smart Search Filters From Aggregate" action is a specialized flow component designed to translate interaction with aggregated data into executable search filters. It functions as a context-aware bridge, taking the context from a user's interaction with an aggregate element and applying it as a filter on a target Data Store.<br/><br/>
This action is tightly coupled to a specific user interface event container, enforced by strict parentage rules that restrict its usage to the TriggerOnAggregateClick event. It is typically used as part of a sequence, commonly preceding a Query action to refresh data based on the newly applied filters.

**Key Features**<br/>
* **Event-Specific Execution:** Implements strict validation logic (isAllowedInsideParent) to ensure it can only be configured within a TriggerOnAggregateClick parent, preventing unsupported usage.
* **Flexible Data Targeting:** Supports applying filters to multiple data store types via the alias property.
* **Filter Management Logic:** Includes a boolean control (clearExistingFilters) to determine whether aggregate-based filters append to, or replace, the current filter state.

**Properties**<br/>
* **Datasource Alias (Required):** A DataSourceAliasChooser property that identifies the Data Store to which the filters will be applied.
  * *Supported Types:* ReadOnlyDataStore, DataStore, LocalDataStore.
* **Clear Existing Filters:** A boolean property.
  * *Function:* Determines whether existing filters should be removed before adding new aggregate-derived filters.

**Use Cases**<br/>
* **Interactive Analytics:** Clicking an aggregate element (e.g., a chart segment) to filter a related grid to show only records contributing to that metric.
* **Drill-Down Navigation:** Refining a search context based on high-level summary data before executing a new query.`,
  };
};

export default ActionAddSmartSearchFiltersFromAggregateManager;
